import { useTranslation } from "react-i18next";
import { ChatStore } from "@/stores/ChatStore";
import { EUserInfoFormStatus } from "@/types";
import { validateField } from "@/lib/validation";

export const useUserInfoDialogFooter = () => {
  const { t } = useTranslation();
  const setUserInfoFormStatus = ChatStore(state => state.setUserInfoFormStatus);
  const user = ChatStore(state => state.user);
  const setError = ChatStore(state => state.setUserInfoFormError);
  const errors = ChatStore(state => state.userInfoFormErrors);

  const hasErrors = () => {
    return Object.values(errors).some(error => error !== "");
  };

  const validateAllFields = () => {
    if (!user) return false;

    const addressFields = [
      { name: "street", value: user.address.street },
      { name: "houseNumber", value: user.address.houseNumber },
      { name: "postCode", value: user.address.postCode },
      { name: "city", value: user.address.city }
    ];
    const insuranceField = { name: "insuranceNumber", value: user.insuranceNumber };
    const dobField = { name: "dateOfBirth", value: user.dateOfBirth };

    // --- Address Group ---
    const addressErrors = addressFields.map(f => ({ name: f.name, error: validateField(f.name, f.value, t) }));
    const isAddressGroupValid = addressErrors.every(e => !e.error);
    const isAddressGroupEmpty = addressFields.every(f => !f.value);

    // --- Insurance Group ---
    const insuranceError = validateField(insuranceField.name, insuranceField.value, t);
    const isInsuranceValid = !insuranceError;
    const isInsuranceEmpty = !insuranceField.value;

    // --- DOB Group ---
    const dobError = validateField(dobField.name, dobField.value, t);
    const isDobValid = !dobError;
    const isDobEmpty = !dobField.value;

    const validGroups = [isAddressGroupValid, isInsuranceValid, isDobValid];
    const emptyGroups = [isAddressGroupEmpty, isInsuranceEmpty, isDobEmpty];
    const validCount = validGroups.filter(Boolean).length;

    let isFormValid = false;
    if (validCount === 3) {
      isFormValid = true;
    } else if (validCount === 2) {
      const invalidIndex = validGroups.findIndex(v => !v);
      if (emptyGroups[invalidIndex]) {
        isFormValid = true;
      }
    }

    // Set errors based on validation state
    if (isAddressGroupValid || (isFormValid && isAddressGroupEmpty)) {
      addressFields.forEach(f => setError(f.name, ""));
    } else {
      addressErrors.forEach(e => {
        if (e.error) setError(e.name, e.error);
      });
    }

    if (isInsuranceValid || (isFormValid && isInsuranceEmpty)) {
      setError(insuranceField.name, "");
    } else {
      if (insuranceError) setError(insuranceField.name, insuranceError);
    }

    if (isDobValid || (isFormValid && isDobEmpty)) {
      setError(dobField.name, "");
    } else {
      if (dobError) setError(dobField.name, dobError);
    }

    return isFormValid;
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();
    
    if (!isValid) {
      return;
    }

    const addressString = user?.address 
      ? `${user.address.street} ${user.address.houseNumber}, ${user.address.postCode} ${user.address.city}`
      : "N/A";
    
    window.alert(
      `${t("userInfoForm.submitting")}\n` +
      `Address: ${addressString}\n` +
      `Insurance Number: ${user?.insuranceNumber || "N/A"}\n` +
      `Date of Birth: ${user?.dateOfBirth || "N/A"}`
    );
    setUserInfoFormStatus(EUserInfoFormStatus.submitted);
  };

  const handleCancel = (onOpenChange: (open: boolean) => void) => {
    onOpenChange(false);
  };
  return {
    t,
    handleSubmit,
    handleCancel,
    hasErrors
  };
};
